import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { usePets } from '../../context/PetsContext';
import { QRCodeCanvas } from '../shared/QRCodeCanvas';

interface CreateQRFlowProps {
  onClose: () => void;
  onComplete: (qrData: Record<string, unknown>) => void;
}

const ACCESS_TYPE_IDS = [
  'view-only',
  'professional-update',
  'temporary-care',
  'emergency-access',
  'ownership-transfer',
] as const;
const ACCESS_TYPE_KEYS: Record<string, string> = {
  'view-only': 'viewOnly',
  'professional-update': 'professionalUpdate',
  'temporary-care': 'temporaryCare',
  'emergency-access': 'emergencyAccess',
  'ownership-transfer': 'ownershipTransfer',
};

const DATA_SCOPE_IDS = [
  'identity',
  'health',
  'physical',
  'medical',
  'grooming',
  'behavioral',
  'documents',
  'family',
  'appointments',
  'payment',
  'ai-insights',
  'microchip',
] as const;
const DATA_SCOPE_KEY_MAP: Record<string, string> = {
  identity: 'scopeIdentity',
  health: 'scopeHealth',
  physical: 'scopePhysical',
  medical: 'scopeMedical',
  grooming: 'scopeGrooming',
  behavioral: 'scopeBehavioral',
  documents: 'scopeDocuments',
  family: 'scopeFamily',
  appointments: 'scopeAppointments',
  payment: 'scopePayment',
  'ai-insights': 'scopeAIInsights',
  microchip: 'microchip',
};

export function CreateQRFlow({ onClose, onComplete }: CreateQRFlowProps) {
  const { t } = useTranslation();
  const { pets } = usePets();

  const accessTypes = ACCESS_TYPE_IDS.map((id) => ({
    id,
    name: t(`features.qrHub.createFlow.${ACCESS_TYPE_KEYS[id]}`),
    description: t(`features.qrHub.createFlow.${ACCESS_TYPE_KEYS[id]}Desc`),
    icon:
      id === 'view-only'
        ? '👁️'
        : id === 'professional-update'
          ? '🏥'
          : id === 'temporary-care'
            ? '🐕‍🦺'
            : id === 'emergency-access'
              ? '🚨'
              : '🔑',
  }));

  const dataScopeOptions = DATA_SCOPE_IDS.map((id) => ({
    id,
    label: t(`features.qrHub.createFlow.${DATA_SCOPE_KEY_MAP[id]}`),
    description: t(`features.qrHub.createFlow.${DATA_SCOPE_KEY_MAP[id]}Desc`),
  }));
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>([]);
  const [accessType, setAccessType] = useState('');
  const [dataScope, setDataScope] = useState<string[]>(['identity', 'health']);
  const [durationType, setDurationType] = useState('time-limited');
  const [durationValue, setDurationValue] = useState('24');
  const [generatedQR, setGeneratedQR] = useState('');
  const [issuedAt, setIssuedAt] = useState('');

  useEffect(() => {
    if (!pets || pets.length === 0) {
      setSelectedPetIds([]);
      return;
    }
    setSelectedPetIds((prev) => {
      const valid = prev.filter((id) => pets.some((pet) => pet.id === id));
      if (valid.length > 0) return valid;
      return [pets[0].id];
    });
  }, [pets]);

  const selectedPetNames = useMemo(() => {
    return selectedPetIds
      .map((id) => pets.find((p) => p.id === id)?.name)
      .filter((name): name is string => Boolean(name));
  }, [selectedPetIds, pets]);

  const qrContent = useMemo(() => {
    if (!generatedQR || selectedPetIds.length === 0) return '';
    const issuedDate = issuedAt ? new Date(issuedAt) : new Date();
    const expiresAt = new Date(issuedDate);
    if (durationType === 'time-limited') {
      expiresAt.setHours(expiresAt.getHours() + parseInt(durationValue));
    } else if (durationType === 'event-based') {
      expiresAt.setDate(expiresAt.getDate() + 1);
    } else if (durationType === 'manual') {
      expiresAt.setFullYear(expiresAt.getFullYear() + 5);
    }
    return JSON.stringify({
      qrId: generatedQR,
      petIds: selectedPetIds,
      accessType,
      dataScope,
      durationType,
      durationValue,
      issuedAt,
      expiresAt: expiresAt.toISOString(),
    });
  }, [
    generatedQR,
    selectedPetIds,
    accessType,
    dataScope,
    durationType,
    durationValue,
    issuedAt,
  ]);

  const totalSteps = 5;

  // Auto-set data scope and duration when Ownership Transfer is selected
  const handleAccessTypeChange = (type: string) => {
    setAccessType(type);
    if (type === 'ownership-transfer') {
      // Select ALL data scopes for ownership transfer
      setDataScope(dataScopeOptions.map((opt) => opt.id));
      // Lock duration to manual revocation
      setDurationType('manual');
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      if (currentStep === 4) {
        const qrId =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `qr-${Date.now()}`;
        setGeneratedQR(qrId);
        setIssuedAt(new Date().toISOString());
      }
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = () => {
    if (selectedPetIds.length === 0) return;
    const expiryDate = new Date();
    if (durationType === 'time-limited') {
      expiryDate.setHours(expiryDate.getHours() + parseInt(durationValue));
    } else if (durationType === 'event-based') {
      expiryDate.setDate(expiryDate.getDate() + 1);
    } else if (durationType === 'manual') {
      expiryDate.setFullYear(expiryDate.getFullYear() + 5);
    }

    const primaryPet =
      pets.find((pet) => pet.id === selectedPetIds[0]) || pets[0];
    const petLabel =
      selectedPetNames.length > 0
        ? selectedPetNames.join(', ')
        : primaryPet?.name || t('common.pet', 'Pet');

    const qrData = {
      id: generatedQR,
      petName: petLabel,
      accessType: accessTypes.find((t) => t.id === accessType)?.name || '',
      dataScope: dataScope.map(
        (s) => dataScopeOptions.find((o) => o.id === s)?.label || ''
      ),
      expiresAt: expiryDate.toISOString(),
      expiryType: durationType,
      scannedCount: 0,
      createdAt: issuedAt || new Date().toISOString(),
      status: 'active' as const,
    };

    onComplete(qrData);
  };

  const toggleDataScope = (scopeId: string) => {
    if (dataScope.includes(scopeId)) {
      setDataScope(dataScope.filter((s) => s !== scopeId));
    } else {
      setDataScope([...dataScope, scopeId]);
    }
  };

  const steps = [
    t('features.qrHub.createFlow.stepSelectPets'),
    t('features.qrHub.createFlow.stepAccessType'),
    t('features.qrHub.createFlow.stepDataScope'),
    t('features.qrHub.createFlow.stepDuration'),
    t('features.qrHub.createFlow.stepGenerateQR'),
  ];

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2
                className="text-2xl mb-1"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.qrHub.createFlow.title')}
              </h2>
              <p
                className="text-sm opacity-90"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('features.qrHub.createFlow.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      index + 1 < currentStep
                        ? 'bg-green-500'
                        : index + 1 === currentStep
                          ? 'bg-white text-indigo-600'
                          : 'bg-white/30'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block">{step}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${index + 1 < currentStep ? 'bg-green-500' : 'bg-white/30'}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Select Pets */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3
                  className="text-xl mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.selectPetProfiles')}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.createFlow.choosePetsToGrant')}
                </p>
              </div>

              <div className="space-y-3">
                {(!pets || pets.length === 0) && (
                  <div
                    className="p-4 border-2 border-dashed rounded-lg text-sm text-gray-600 dark:text-white/60"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('features.qrHub.createFlow.noPetProfilesFound')}
                  </div>
                )}
                {pets?.map((pet) => {
                  const emoji =
                    pet.species === 'dog'
                      ? '🐕'
                      : pet.species === 'cat'
                        ? '🐈'
                        : '🐾';
                  const isSelected = selectedPetIds.includes(pet.id);
                  return (
                    <label
                      key={pet.id}
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPetIds([...selectedPetIds, pet.id]);
                          } else {
                            setSelectedPetIds(
                              selectedPetIds.filter((id) => id !== pet.id)
                            );
                          }
                        }}
                        className="w-5 h-5 text-indigo-600"
                      />
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center text-2xl">
                        {emoji}
                      </div>
                      <div className="flex-1">
                        <div
                          className="mb-1 text-gray-900 dark:text-white"
                          style={{
                            fontFamily: 'Sora, sans-serif',
                            fontWeight: 600,
                          }}
                        >
                          {pet.name}
                        </div>
                        <div
                          className="text-sm text-gray-600 dark:text-white/50"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {pet.breed ||
                            t('petOverview.notSpecified', 'Unknown breed')}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Access Type */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <h3
                  className="text-xl mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.chooseAccessType')}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.createFlow.selectAccessLevel')}
                </p>
              </div>

              <div className="space-y-3">
                {accessTypes.map((type) => (
                  <label
                    key={type.id}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                      accessType === type.id
                        ? 'border-indigo-600 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-300 dark:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="accessType"
                      value={type.id}
                      checked={accessType === type.id}
                      onChange={(e) => handleAccessTypeChange(e.target.value)}
                      className="w-5 h-5 text-indigo-600 mt-1"
                    />
                    <div className="text-3xl">{type.icon}</div>
                    <div className="flex-1">
                      <div
                        className="mb-1 text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {type.name}
                      </div>
                      <div
                        className="text-sm text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {type.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Data Scope */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <h3
                  className="text-xl mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.selectDataScope')}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.createFlow.chooseWhatToShare')}
                </p>
                {accessType === 'ownership-transfer' && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-500/30 rounded-lg p-3 mt-3">
                    <p
                      className="text-sm text-orange-800 dark:text-orange-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      🔑 {t('features.qrHub.createFlow.ownershipTransferNote')}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {dataScopeOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      accessType === 'ownership-transfer'
                        ? 'bg-gray-100 dark:bg-white/5 border-gray-300 dark:border-white/10 cursor-not-allowed'
                        : 'hover:bg-gray-50 dark:hover:bg-white/5 border-gray-300 dark:border-white/20'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={dataScope.includes(option.id)}
                      onChange={() =>
                        accessType !== 'ownership-transfer' &&
                        toggleDataScope(option.id)
                      }
                      disabled={accessType === 'ownership-transfer'}
                      className="w-5 h-5 text-indigo-600 mt-1"
                    />
                    <div className="flex-1">
                      <div
                        className="mb-1 text-gray-900 dark:text-white"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {option.label}
                      </div>
                      <div
                        className="text-sm text-gray-600 dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {option.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Duration */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <h3
                  className="text-xl mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.accessDuration')}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.createFlow.setHowLongValid')}
                </p>
                {accessType === 'ownership-transfer' && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-500/30 rounded-lg p-3 mt-3">
                    <p
                      className="text-sm text-orange-800 dark:text-orange-200"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      🔑 {t('features.qrHub.createFlow.durationLockedNote')}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg transition-colors ${
                    accessType === 'ownership-transfer'
                      ? 'border-gray-300 dark:border-white/10 bg-gray-100 dark:bg-white/5 cursor-not-allowed opacity-60'
                      : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 border-gray-300 dark:border-white/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="duration"
                    value="time-limited"
                    checked={durationType === 'time-limited'}
                    onChange={(e) => setDurationType(e.target.value)}
                    disabled={accessType === 'ownership-transfer'}
                    className="w-5 h-5 text-indigo-600 mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <div
                      className="mb-2 text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('features.qrHub.createFlow.timeLimited')}
                    </div>
                    <select
                      value={durationValue}
                      onChange={(e) => setDurationValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      disabled={
                        durationType !== 'time-limited' ||
                        accessType === 'ownership-transfer'
                      }
                    >
                      <option value="1">
                        {t('features.qrHub.createFlow.duration1h')}
                      </option>
                      <option value="4">
                        {t('features.qrHub.createFlow.duration4h')}
                      </option>
                      <option value="8">
                        {t('features.qrHub.createFlow.duration8h')}
                      </option>
                      <option value="24">
                        {t('features.qrHub.createFlow.duration24h')}
                      </option>
                      <option value="72">
                        {t('features.qrHub.createFlow.duration3d')}
                      </option>
                      <option value="168">
                        {t('features.qrHub.createFlow.duration1w')}
                      </option>
                    </select>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg transition-colors ${
                    accessType === 'ownership-transfer'
                      ? 'border-gray-300 dark:border-white/10 bg-gray-100 dark:bg-white/5 cursor-not-allowed opacity-60'
                      : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 border-gray-300 dark:border-white/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="duration"
                    value="event-based"
                    checked={durationType === 'event-based'}
                    onChange={(e) => setDurationType(e.target.value)}
                    disabled={accessType === 'ownership-transfer'}
                    className="w-5 h-5 text-indigo-600 mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <div className="flex-1">
                    <div
                      className="mb-1 text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('features.qrHub.createFlow.eventBased')}
                    </div>
                    <div
                      className="text-sm text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.createFlow.eventBasedDesc')}
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg transition-colors ${
                    accessType === 'ownership-transfer'
                      ? 'border-orange-300 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-900/20'
                      : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 border-gray-300 dark:border-white/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="duration"
                    value="manual"
                    checked={durationType === 'manual'}
                    onChange={(e) => setDurationType(e.target.value)}
                    className="w-5 h-5 text-indigo-600 mt-1"
                  />
                  <div className="flex-1">
                    <div
                      className="mb-1 text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {accessType === 'ownership-transfer'
                        ? t(
                            'features.qrHub.createFlow.manualRevocationRecommended'
                          )
                        : t('features.qrHub.createFlow.untilManuallyRevoked')}
                    </div>
                    <div
                      className="text-sm text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {accessType === 'ownership-transfer'
                        ? t('features.qrHub.createFlow.ownershipRevocationDesc')
                        : t('features.qrHub.createFlow.manualRevocationDesc')}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 5: Generated QR */}
          {currentStep === 5 && (
            <div className="space-y-6 text-center">
              <div className="w-64 h-64 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl mx-auto flex items-center justify-center shadow-lg">
                {qrContent ? (
                  <QRCodeCanvas
                    value={qrContent}
                    size={216}
                    className="bg-white dark:bg-[#0F0E16] p-4 rounded-xl shadow-inner"
                  />
                ) : (
                  <div className="text-sm text-indigo-600 dark:text-indigo-300">
                    {t('features.qrHub.createFlow.generatingQR')}
                  </div>
                )}
              </div>

              <div>
                <h3
                  className="text-xl mb-2 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.qrCodeGenerated')}
                </h3>
                <p
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('features.qrHub.createFlow.shareCodeToGrant')}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 max-w-md mx-auto text-left border dark:border-white/8">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span
                      className="text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.createFlow.petsLabel')}
                    </span>
                    <span
                      className="text-gray-900 dark:text-white text-right"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {selectedPetNames.length > 0
                        ? selectedPetNames.join(', ')
                        : t('features.qrHub.createFlow.noneSelected')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className="text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.createFlow.accessTypeLabel')}
                    </span>
                    <span
                      className="text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {accessTypes.find((type) => type.id === accessType)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className="text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.createFlow.dataScopeLabel')}
                    </span>
                    <span
                      className="text-gray-900 dark:text-white"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('features.qrHub.createFlow.modulesCount', {
                        count: dataScope.length,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className="text-gray-600 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.qrHub.createFlow.durationLabel')}
                    </span>
                    <span
                      className="text-gray-900 dark:text-white text-right"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {durationType === 'time-limited'
                        ? t(
                            `features.qrHub.createFlow.duration${durationValue === '72' ? '3d' : durationValue === '168' ? '1w' : durationValue}h`
                          )
                        : durationType === 'event-based'
                          ? t('features.qrHub.createFlow.afterEventCompletes')
                          : t(
                              'features.qrHub.createFlow.untilManuallyRevokedShort'
                            )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 max-w-md mx-auto">
                <button
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.downloadQR')}
                </button>
                <button
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.shareLink')}
                </button>
                <button
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.print')}
                </button>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4 max-w-md mx-auto">
                <p
                  className="text-sm text-yellow-800 dark:text-yellow-200"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  ⚠️ {t('features.qrHub.createFlow.revokeWarning')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-white/8 p-6">
          <div className="flex justify-between">
            {currentStep > 1 && currentStep < 5 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 cursor-pointer"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                <ChevronLeft className="w-4 h-4" />
                {t('features.qrHub.createFlow.back')}
              </button>
            )}

            <div className="ml-auto flex gap-3">
              {currentStep < 5 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && selectedPetIds.length === 0) ||
                    (currentStep === 2 && !accessType) ||
                    (currentStep === 3 && dataScope.length === 0)
                  }
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.qrHub.createFlow.next')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <Check className="w-4 h-4" />
                  {t('features.qrHub.createFlow.complete')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
